import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import { apiGet, apiSend } from '../api';
import { alertFailure } from '../utils/sessionEnd';
import { backfillPhotoVariants } from '../utils/backfillPhotoVariants';
import { theme, card, btnPrimary, btnSecondary } from '../theme';
// v0.46.7 — Shared page title (standardised heading size).
import PageHeading from '../components/PageHeading';
// v0.17.1 — Lucide migration.
import { FileText, Settings, Link2, Send, Palette } from 'lucide-react';
// v0.32.0 — Shared three-button motto component (Drop a Plan / Drop a Pin /
// Send a Report) so the trio reads identically across every screen.
import MottoButtons from '../components/MottoButtons';
import { buildDefectReportPdf } from '../utils/defect-report-template.js';
import { loadReportImages } from '../utils/reportImages.js';
import { DEFAULT_SETTINGS, normaliseDefect, normalisePhoto, normaliseDrawing, reportBrand, settingsFromSavedReport } from '../utils/reportPdf.js';
import ShareReportModal from '../components/ShareReportModal';
import { TRADES, tradeLabel, tradeColor } from '../utils/trades.js';
// v0.10.0 - per-report distribution roster, cloned from the project
// team at report-creation time, edited per-report afterwards.
import TeamPanel from '../components/TeamPanel';
// v0.37.0 — Used to default the "Prepared by" field to the signed-in user.
import { getCurrentUser } from '../utils/user';
// v0.38.0 — Subscription tier feeds the PDF builder so Free tier stamps
//           a "DROPPIN" watermark on body pages; Pro tier suppresses it.
import { getTenantTier } from '../utils/tier';
// v0.103.0 — Report layout styles: six presentation styles + accent
//            colour, chosen in a visual picker with live previews.
import ReportStyleModal from '../components/ReportStyleModal';
import { resolveReportStyle, loadStylePrefs, saveStylePrefs } from '../utils/report-styles';
// v0.312.0 — the status filter offers the five keys (was open /
// in_progress / rectified: no Complete, and a dead In Progress).
import { STATUSES, statusLabel } from '../utils/defectStatus';

// ---------------------------------------------------------------------------
// ReportScreen — the headline DropPin output. "Drop a plan. Drop a pin.
// SEND A REPORT." This is the third step.
//
// What it does:
//   1. Fetches project + all drawings + all defects (with photos inlined)
//   2. Builds a client-side jsPDF using ../utils/defect-report-template
//   3. Renders the result in a preview iframe + offers Download / Share
//
// Routes:
//   /projects/:projectId/report               full project report (live one-off)
//   /projects/:projectId/report?defectIds=1,2 only those defects (set by
//                                             RegisterScreen's "Selected to PDF")
//   /projects/:projectId/report?reportId=N    open a saved Report row's
//                                             settings (v0.9.0) - the row
//                                             from /api/reports drives the
//                                             filter + section state.
//
// v0.7.0 — initial.
// v0.8.0 — share button opens <ShareReportModal>.
// v0.9.0 — settings drawer (filters + section toggles + paper + client name
//          + title). reportId query loads a saved Reports row. Settings are
//          threaded into buildDefectReportPdf, which filters BEFORE render.
//          normaliseDrawing now keeps rasterUrl/sourceUrl so the plan
//          section actually has an image to draw (v0.7.0 silently dropped
//          them by mapping only fileDataUrl, which the API never returns).
// ---------------------------------------------------------------------------
// (DEFAULT_SETTINGS and the row normalisers live in utils/reportPdf.js since v0.218.0.)
const PRIORITIES = ['low', 'medium', 'high', 'urgent'];
const PAPER_SIZES = [{ value: 'a4', label: 'A4' }, { value: 'letter', label: 'Letter' }];

function priorityLabel(p) {
  return ({ low: 'Low', medium: 'Medium', high: 'High', urgent: 'Urgent' })[p] || p;
}

export default function ReportScreen() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const defectIdsParam = searchParams.get('defectIds');
  const defectIdsFilter = useMemo(() => {
    if (!defectIdsParam) return null;
    return defectIdsParam.split(',').map(s => Number(s)).filter(Number.isFinite);
  }, [defectIdsParam]);
  // v0.9.0 — optional saved-report binding.
  const reportIdParam = searchParams.get('reportId');
  const reportId = reportIdParam ? Number(reportIdParam) : null;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // v0.45.29 — Stage tracker drives the loading-skeleton checklist so
  // the user sees which step is in flight rather than a generic spinner.
  // Values, in order: 'project' → 'photos' → 'report' → 'team' → 'done'.
  const [loadStage, setLoadStage] = useState('project');
  const [project, setProject] = useState(null);
  const [drawings, setDrawings] = useState([]);
  const [defects, setDefects] = useState([]);
  const [building, setBuilding] = useState(false);
  // v0.102.0 — one-off rebuild of the downscaled photo copies for reports whose
  // photos were captured before v0.101.0 started generating them.
  const [optimising, setOptimising] = useState(false);
  const [optimiseMsg, setOptimiseMsg] = useState(null);

  // Rebuild the downscaled copies for every photo on this report. Reads the
  // photo rows fresh rather than trusting the render-shaped copies held in
  // `defects`, so it always sees the true thumbnailUrl/displayUrl state.
  const optimisePhotos = async () => {
    if (optimising) return;
    setOptimising(true);
    setOptimiseMsg('Reading photos…');
    try {
      const rows = [];
      for (const d of defects) {
        if (!d || !d.id) continue;
        try {
          // eslint-disable-next-line no-await-in-loop
          const ph = await apiGet(`/api/defect-photos?defectId=${d.id}`);
          if (Array.isArray(ph)) rows.push(...ph);
        } catch (_e) { /* one defect's photos failing must not stop the rest */ }
      }
      if (!rows.length) { setOptimiseMsg('No photos to optimise.'); return; }
      const res = await backfillPhotoVariants(
        rows,
        (photoId, patch) => apiSend('PATCH', `/api/defect-photos/${photoId}`, patch),
        (p) => setOptimiseMsg(`Optimising ${p.done} of ${p.total}…`),
      );
      setOptimiseMsg(
        `${res.updated} optimised` +
        (res.skipped ? `, ${res.skipped} already done` : '') +
        (res.failed ? `, ${res.failed} failed` : '') + '.',
      );
    } catch (e) {
      setOptimiseMsg('Could not optimise: ' + (e && e.message ? e.message : 'unknown error'));
    } finally {
      setOptimising(false);
    }
  };
  const [pdfUrl, setPdfUrl] = useState(null);     // object URL for iframe + download
  const [pdfDoc, setPdfDoc] = useState(null);     // raw jsPDF (so Download can re-save)
  const [buildError, setBuildError] = useState(null);
  // v0.215.0 — "download pdf doesnt give me any images". Pictures are fetched
  // and prepared before the PDF is built (reportImages.js); these say how far
  // that has got, and which pictures could not be had and why, so a missing
  // photo is reported rather than printed as an empty box.
  const [imageProgress, setImageProgress] = useState(null);
  const [imageProblems, setImageProblems] = useState([]);
  // Prepared once per set of photos/plans/logo; a style or settings rebuild
  // reuses them instead of downloading every photo again.
  const preparedImages = useRef({ key: null, value: null });
  const [showShareModal, setShowShareModal] = useState(false);
  // v0.92.0 — "Send to InSite": confirm + POST this report to /api/raise-enquiry,
  // which builds the InSpec intake payload and holds the shared secret server-side.
  const [showSendModal, setShowSendModal] = useState(false);

  // v0.9.0 — settings drawer state.
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  // v0.45.10 — `?settings=open` URL flag opens the panel on mount so
  //             the RegisterScreen "⚙ Settings" button deep-links
  //             into the panel without an extra click.
  const [showSettings, setShowSettings] = useState(() => {
    try {
      return new URLSearchParams(window.location.search).get('settings') === 'open';
    } catch { return false; }
  });
  const [savedReport, setSavedReport] = useState(null);
  // v0.307.0 — Brett: "Shift the archive button to be in settings". Archive
  // moved off the register's top bar into this panel. Same soft archive as
  // before (archived: true merged into the report's settingsJson), merged into
  // the report as the server holds it now so no other saved setting is lost.
  async function archiveReport() {
    if (!window.confirm("Archive this report? It will hide from the project's reports list but stays recoverable.")) return;
    try {
      const cur = await apiGet(`/api/reports/${reportId}`);
      const raw = cur?.settingsJson;
      const prev = raw && typeof raw === 'object' ? raw : (() => { try { return JSON.parse(raw || '{}'); } catch { return {}; } })();
      await apiSend('PATCH', `/api/reports/${reportId}`, { settingsJson: { ...prev, archived: true } });
      navigate(`/projects/${projectId}`);
    } catch (e) {
      alertFailure('Could not archive: ' + (e.message || e), e);
    }
  }
  const [savingReport, setSavingReport] = useState(false);
  // v0.103.0 — Report style chooser modal.
  const [showStyleModal, setShowStyleModal] = useState(false);
  // v0.40.0 — Distribution roster threaded into the PDF builder.
  const [reportTeamMembers, setReportTeamMembers] = useState([]);
  // v0.42.0 — Tenant brand (name / colour / logo) for the white-label
  //           PDF render. Read on Pro; ignored on Free.
  const [tenant, setTenant] = useState(null);

  // ---- Load project + drawings + defects + photos + (optional) saved report
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null); setLoadStage('project');
      try {
        // v0.45.32 — Brett (marked-up PDF showing 5 drawings + 7
        // defects on a job that should have 1 + 2): the report was
        // pulling all PROJECT-scoped drawings / defects instead of
        // the report-scoped subset. v0.22.0 schema already has
        // Drawings.ReportId + Defects.ReportId; the backend already
        // supports ?reportId=N; the client just wasn't using it.
        // Fixed: when a reportId is present, query by reportId.
        const drawingsUrl = reportId
          ? `/api/drawings?reportId=${encodeURIComponent(reportId)}`
          : `/api/drawings?projectId=${projectId}`;
        const defectsUrl = reportId
          ? `/api/defects?reportId=${encodeURIComponent(reportId)}`
          : `/api/defects?projectId=${projectId}`;
        const [proj, draws, defs] = await Promise.all([
          apiGet(`/api/projects/${projectId}`),
          apiGet(drawingsUrl),
          apiGet(defectsUrl),
        ]);

        // 2026-09-15 — Brett: "do the pdf too". Each drawing's markup (measured
        // areas, notes, the Legend stamp) for the PDF's plan page. Read fresh: the
        // service worker's copy could be a markup from before the last edit. A
        // drawing with no markup answers 404; anything else is reported, and the
        // report still builds without the overlay.
        const drawsWithMarkup = await Promise.all((draws || []).map(async (dr) => {
          const id = dr && (dr.id ?? dr.Id);
          if (id == null) return dr;
          try {
            const j = await apiGet(`/api/markup?drawingId=${encodeURIComponent(id)}`, { fresh: true });
            return { ...dr, markup: (j && j.doc) || null };
          } catch (e) {
            if (!e || e.status !== 404) console.warn('[report] markup for drawing', id, 'could not be read:', e && e.message);
            return { ...dr, markup: null };
          }
        }));

        // Hydrate photos per defect.
        if (!cancelled) setLoadStage('photos');
        const defectsWithPhotos = await Promise.all(
          (defs || []).map(async (d) => {
            const norm = normaliseDefect(d);
            if (!norm.photoCount || norm.photoCount === 0) {
              return { ...norm, photos: [] };
            }
            try {
              const ph = await apiGet(`/api/defect-photos?defectId=${norm.id}`);
              return { ...norm, photos: Array.isArray(ph) ? ph.map(normalisePhoto) : [] };
            } catch {
              return { ...norm, photos: [] };
            }
          })
        );

        // v0.9.0 — if ?reportId=N, fetch the saved Report row and merge its
        // settingsJson onto the defaults. Tolerates missing/bad JSON.
        // v0.103.0 — the user's sticky style preference (style + accent
        // colour, droppin.reportStyle.v1) layers over the defaults first;
        // a saved report's own settingsJson still wins over both.
        let initSettings = { ...DEFAULT_SETTINGS, ...loadStylePrefs() };
        let saved = null;
        if (reportId) {
          if (!cancelled) setLoadStage('report');
          try {
            saved = await apiGet(`/api/reports/${reportId}`);
            // v0.218.0 — one rule for this and the shared report's PDF (reportPdf.js):
            // the row's settingsJson wins, the name is the title when none was set,
            // and the row's own PreparedBy wins over the current-user default.
            initSettings = settingsFromSavedReport(saved, initSettings);
          } catch (_e) {
            // Bad reportId — fall back to defaults; show banner via savedReport=null.
          }
        }

        // v0.37.0 — default preparedBy from the logged-in user if the saved
        // row didn't have one (and the user has a name we can use).
        if (!initSettings.preparedBy) {
          const u = getCurrentUser();
          const guess = u?.name || u?.email || '';
          if (guess) initSettings = { ...initSettings, preparedBy: guess };
        }

        if (cancelled) return;
        setProject(proj);
        setDrawings(drawsWithMarkup.map(normaliseDrawing));
        setDefects(defectsWithPhotos);
        setSettings(initSettings);
        setSavedReport(saved);

        // v0.42.0 — Tenant brand for white-label rendering.
        try {
          const t = await apiGet('/api/tenant');
          if (!cancelled) setTenant(t);
        } catch { if (!cancelled) setTenant(null); }

        // v0.40.0 — Pull the per-report distribution roster so we can
        // render the Distribution section on the PDF cover.
        if (reportId) {
          if (!cancelled) setLoadStage('team');
          try {
            const team = await apiGet(`/api/report-team?reportId=${encodeURIComponent(reportId)}`);
            if (!cancelled) setReportTeamMembers(Array.isArray(team) ? team : []);
          } catch { if (!cancelled) setReportTeamMembers([]); }
        } else {
          setReportTeamMembers([]);
        }
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) { setLoadStage('done'); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [projectId, reportId]);

  // ---- Build the PDF on demand ----
  // v0.103.0 — optional settings override so the style picker can apply
  // + rebuild in one step (React state lands asynchronously; passing the
  // fresh settings avoids rendering with the stale closure).
  const generate = async (overrideSettings) => {
    // Guard: onClick handlers pass a DOM event — only accept a real
    // settings object (it always carries styleKey after v0.103.0).
    const s = (overrideSettings && overrideSettings.styleKey) ? overrideSettings : settings;
    setBuilding(true); setBuildError(null);
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    try {
      // v0.42.0 — Tier-gated brand. Pro uses the tenant's brand name +
      //           colour + logo; Free always falls back to DEFAULT_BRAND
      //           so the DropPin watermark + wordmark stay regardless of
      //           what the user has set in /settings.
      const tier = getTenantTier();
      const brand = reportBrand({ tier, tenant, settings: s });

      // v0.215.0 — fetch every picture FIRST. jsPDF must never be handed an
      // address: it fetches it badly and, when that fails, silently embeds the
      // address text as the image (reportImages.js has the whole story).
      const imageKey = JSON.stringify([
        (drawings || []).map((dr) => [dr.id, dr.rasterUrl, dr.sourceUrl, dr.pageNumber]),
        (defects || []).map((d) => [d.id, (d.photos || []).map((p) => p.id)]),
        brand.logoDataUrl || null,
      ]);
      let prepared = preparedImages.current.key === imageKey ? preparedImages.current.value : null;
      if (!prepared) {
        setImageProgress({ done: 0, total: 0 });
        prepared = await loadReportImages(
          { drawings, defects, logoUrl: brand.logoDataUrl || null },
          undefined,
          { onProgress: (pr) => setImageProgress(pr) },
        );
        preparedImages.current = { key: imageKey, value: prepared };
      }
      setImageProgress(null);
      setImageProblems(prepared.problems);
      if (prepared.problems.length) console.warn('[report] pictures that could not be prepared:', prepared.problems);
      brand.logoDataUrl = prepared.logo;

      const doc = await buildDefectReportPdf({
        project,
        drawings: prepared.drawings,
        defects: prepared.defects,
        brand,
        defectIdsFilter,
        // v0.40.0 — Distribution list rendered on the cover.
        reportTeamMembers,
        settings: { ...s, tier },
      });
      const blob = doc.output('blob');
      const url = URL.createObjectURL(blob);
      setPdfDoc(doc);
      setPdfUrl(url);
    } catch (e) {
      console.error('PDF build failed', e);
      setBuildError(e.message || String(e));
    } finally {
      setBuilding(false);
    }
  };

  // Auto-build on first load once data is in.
  useEffect(() => {
    if (!loading && !error && project && !pdfUrl && !building) {
      generate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, project]);

  useEffect(() => () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);

  const download = () => {
    if (!pdfDoc || !project) return;
    const safe = (project.Name || project.name || 'project').replace(/[^A-Za-z0-9_-]+/g, '_');
    const suffix = defectIdsFilter && defectIdsFilter.length ? `_${defectIdsFilter.length}-selected` : '';
    pdfDoc.save(`DefectReport_${safe}${suffix}.pdf`);
  };

  const share = () => setShowShareModal(true);

  // v0.9.0 — save current settings back to the bound Report row. Only
  // available when ?reportId=N is in the URL.
  // v0.37.0 — preparedBy patched onto its own column (not bundled in
  // settingsJson) so it's queryable + survives settings shape changes.
  const saveSettings = async () => {
    if (!reportId) return;
    setSavingReport(true);
    try {
      // Strip preparedBy out of the JSON blob — it has its own column.
      const { preparedBy, ...settingsForJson } = settings || {};
      const updated = await apiSend('PATCH', `/api/reports/${reportId}`, {
        settingsJson: settingsForJson,
        preparedBy: preparedBy || null,
      });
      setSavedReport(updated);
    } catch (e) {
      setBuildError('Could not save: ' + (e.message || String(e)));
    } finally {
      setSavingReport(false);
    }
  };

  // ---- Settings update helpers ----
  const toggleArrayItem = (arr, item) => {
    const set = new Set(arr || []);
    if (set.has(item)) set.delete(item); else set.add(item);
    return Array.from(set);
  };
  const updateFilter = (key, value) => {
    setSettings(s => ({ ...s, filters: { ...s.filters, [key]: value } }));
  };

  // v0.298.0 — Brett: "i need to be able to go back to the report register.
  // not to saved reports." A saved report goes back to ITS register (the item
  // list Send a Report was pressed from), not the list of saved reports.
  const backTo = reportId
    ? `/projects/${projectId}/register?reportId=${encodeURIComponent(reportId)}`
    : `/projects/${projectId}`;

  // ---- Render ----
  if (loading) {
    return <ReportLoadingState
      stage={loadStage}
      onBack={() => navigate(backTo)}
      hasReportId={!!reportId}
    />;
  }
  if (error) {
    return (
      <div>
        <button onClick={() => navigate(backTo)} style={{ ...btnSecondary, marginBottom: 12 }}>← Back</button>
        <div style={{ ...card, color: theme.danger }}>{error}</div>
      </div>
    );
  }

  const totalDefects = defectIdsFilter && defectIdsFilter.length
    ? defectIdsFilter.length
    : defects.length;

  return (
    <div>
      <button
        onClick={() => navigate(backTo)}
        style={{ ...btnSecondary, marginBottom: 12 }}
      >← Back {reportId ? 'to register' : 'to project'}</button>

      <div style={{ ...card, marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <PageHeading style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <FileText size={20} /> {savedReport ? savedReport.name : (settings.title || 'Defect report')}
            </PageHeading>
            <div style={{ fontSize: 13, color: theme.textLight, marginTop: 4 }}>
              {project?.Name || project?.name || 'Location'} · {drawings.length} drawing{drawings.length === 1 ? '' : 's'} · {totalDefects} defect{totalDefects === 1 ? '' : 's'}
              {defectIdsFilter && defectIdsFilter.length ? <span style={{ color: theme.brand, fontWeight: 700 }}> · filtered to selected</span> : null}
              {savedReport && <span style={{ color: theme.brand, fontWeight: 700 }}> · saved report</span>}
            </div>
            {savedReport?.description && (
              <div style={{ fontSize: 12, color: theme.textLight, marginTop: 4, fontStyle: 'italic' }}>
                {savedReport.description}
              </div>
            )}
          </div>
          {/* v0.32.0 — Motto trio (Drop a Plan / Drop a Pin / Send a
              Report) up top for consistent cross-navigation. The
              report-specific actions (Settings / Generate / Download /
              Share) sit on the row below so they stay close to the
              preview they affect, but the user always has a clear way
              back to the other two motto steps. */}
          <MottoButtons
            onDropPlan={() => navigate(`/projects/${projectId}${reportId ? `?reportId=${encodeURIComponent(reportId)}` : ''}`)}
            onDropPin={() => navigate(`/projects/${projectId}/plan${reportId ? `?reportId=${encodeURIComponent(reportId)}` : ''}`)}
            onSendReport={() => { /* already here */ }}
            disable={{ report: true }}
            primaryKey="report"
          />
        </div>
        {/* v0.45.3 — Surface the format picker on the main toolbar so
            it's discoverable without opening Settings. Brett: "I still
            can't see any report themes done." The same picker remains
            inside SettingsPanel for the keyboard-driven flow + radio
            descriptions; this is the at-a-glance chip row. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: theme.textLight, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Format:</span>
          {[
            { value: 'snag-list',         label: 'Snag list' },
            { value: 'client-handover',   label: 'Client handover' },
            { value: 'subbie-work-order', label: 'Subbie work order' },
          ].map(opt => {
            const active = settings.format === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setSettings(x => ({ ...x, format: opt.value }))}
                style={{
                  padding: '4px 10px', fontSize: 12, fontWeight: 600,
                  border: `1px solid ${active ? theme.brand : theme.border}`,
                  background: active ? theme.brand : theme.surface,
                  color: active ? 'white' : theme.text,
                  borderRadius: 999, cursor: 'pointer',
                  fontFamily: 'inherit',
                }}
              >{opt.label}</button>
            );
          })}
        </div>
        {/* Report-specific action row (was the top-right group). */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10, alignItems: 'center' }}>
          {/* v0.103.0 — Report style chooser. Visual picker with live
              miniature previews of all six layout styles + accent
              colour. The swatch shows the current accent at a glance. */}
          <button
            onClick={() => setShowStyleModal(true)}
            style={{ ...btnSecondary, display: 'inline-flex', alignItems: 'center', gap: 6 }}
            title="Choose the report layout style and accent colour"
          >
            <Palette size={14} />
            Style: {resolveReportStyle(settings.styleKey).name}
            <span style={{
              width: 10, height: 10, borderRadius: 3, display: 'inline-block',
              background: settings.brandColor || theme.brand,
              border: '1px solid rgba(0,0,0,0.15)',
            }} />
          </button>
          <button
            onClick={() => setShowSettings(v => !v)}
            style={{ ...btnSecondary, background: showSettings ? theme.bg : btnSecondary.background, display: 'inline-flex', alignItems: 'center', gap: 6 }}
            title="Toggle the report settings panel"
          >
            <Settings size={14} /> Settings
          </button>
          <button onClick={generate} disabled={building} style={{ ...btnSecondary, opacity: building ? 0.6 : 1, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            {building && (
              <span
                aria-hidden
                style={{
                  display: 'inline-block', width: 12, height: 12,
                  border: '2px solid ' + theme.brand,
                  borderTopColor: 'transparent',
                  borderRadius: '50%',
                  animation: 'dpReportSpin 0.8s linear infinite',
                }}
              />
            )}
            {building ? 'Building…' : pdfUrl ? 'Rebuild' : 'Generate'}
          </button>
          <button onClick={download} disabled={!pdfDoc || building} style={{ ...btnPrimary, opacity: (pdfDoc && !building) ? 1 : 0.5 }}>
            ⬇ Download PDF
          </button>
          {/* v0.102.0 — Optimise photos. Photos taken before v0.101.0 carry only
              the camera original, which is what made the PDF unemailable and the
              InSite enquiry payload 30 MB. This rebuilds the two downscaled
              copies in the browser, using the SAME downscaler a fresh capture
              uses. Originals are never modified, and it is safe to re-run. */}
          <button
            onClick={optimisePhotos}
            disabled={optimising || !defects.length}
            style={{ ...btnSecondary, display: 'inline-flex', alignItems: 'center', gap: 6,
                     opacity: (optimising || !defects.length) ? 0.5 : 1 }}
            title="Rebuild the small and mid-size copies of this report's photos. Run this before sending or downloading if the photos are old."
          >
            {optimising ? 'Optimising…' : '⚙ Optimise photos'}
          </button>
          {optimiseMsg && (
            <span style={{ fontSize: 12, color: theme.textLight, alignSelf: 'center' }}>{optimiseMsg}</span>
          )}
          <button onClick={share} style={{ ...btnSecondary, display: 'inline-flex', alignItems: 'center', gap: 6 }} title="Mint a read-only share link">
            <Link2 size={14} /> Share link
          </button>
          {/* v0.92.0 — Send this report to the InSite office as a new enquiry
              (lands in the Mail Room). Server-side send; independent of the
              client-built PDF. */}
          <button onClick={() => setShowSendModal(true)} style={{ ...btnSecondary, display: 'inline-flex', alignItems: 'center', gap: 6 }} title="Send this report to the InSite office as a new enquiry">
            <Send size={14} /> Send to InSite
          </button>
          {/* v0.45.3 — Inline status while building. Sits next to the
              action buttons so the operator sees activity without
              hunting for it. The full-width progress banner over the
              preview area handles the "where did my PDF go" question. */}
          {building && (
            <span style={{ fontSize: 12, color: theme.brand, fontWeight: 600 }}>
              {imageProgress && imageProgress.total
                ? `Getting photos and plans — ${imageProgress.done} of ${imageProgress.total}. Keep this tab open.`
                : 'Rendering PDF — keep this tab open.'}
            </span>
          )}
        </div>
        {/* v0.45.3 — Spinner keyframes. Inline so the report screen
            doesn't depend on a global CSS file. */}
        <style>{`@keyframes dpReportSpin { from { transform: rotate(0); } to { transform: rotate(360deg); } }`}</style>
        {buildError && (
          <div style={{ marginTop: 10, color: theme.danger, fontSize: 13 }}>{buildError}</div>
        )}
        {!building && imageProblems.length > 0 && (
          <div style={{ marginTop: 10, color: theme.text, fontSize: 13, borderLeft: `3px solid ${theme.warn}`, paddingLeft: 10 }}>
            {imageProblems.length === 1 ? '1 picture' : `${imageProblems.length} pictures`} could not be put in the PDF, and say so where they belong.
            <ul style={{ margin: '4px 0 0', paddingLeft: 18, color: theme.textLight, fontSize: 12 }}>
              {imageProblems.slice(0, 6).map((p, i) => <li key={i}>{p}</li>)}
              {imageProblems.length > 6 && <li>and {imageProblems.length - 6} more</li>}
            </ul>
          </div>
        )}
      </div>

      {showSettings && (
        <SettingsPanel
          settings={settings}
          setSettings={setSettings}
          updateFilter={updateFilter}
          toggleArrayItem={toggleArrayItem}
          drawings={drawings}
          onClose={() => setShowSettings(false)}
          onSave={reportId ? saveSettings : null}
          savingReport={savingReport}
          onOpenStyle={() => setShowStyleModal(true)}
          onArchive={reportId ? archiveReport : null}
        />
      )}

      <div style={{ ...card, padding: 0, overflow: 'hidden', height: '78vh', background: '#52525b', position: 'relative' }}>
        {building && !pdfUrl ? (
          // v0.45.3 — First-build state. Big visible spinner +
          // explanatory copy instead of a one-line "Rendering PDF…"
          // that Brett missed.
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'white', gap: 14 }}>
            <span
              aria-hidden
              style={{
                display: 'inline-block', width: 56, height: 56,
                border: '5px solid rgba(255,255,255,0.15)',
                borderTopColor: theme.brand,
                borderRadius: '50%',
                animation: 'dpReportSpin 0.9s linear infinite',
              }}
            />
            <div style={{ fontSize: 16, fontWeight: 700 }}>Rendering PDF…</div>
            <div style={{ fontSize: 12, opacity: 0.8, maxWidth: 360, textAlign: 'center', lineHeight: 1.4 }}>
              Compiling drawings, defect cards and plan pages. Large reports
              (50+ defects with photos) can take a minute. Keep this tab open.
            </div>
          </div>
        ) : pdfUrl ? (
          <>
            <iframe
              title="Defect report preview"
              src={pdfUrl}
              style={{ width: '100%', height: '100%', border: 0, background: '#52525b' }}
            />
            {/* v0.45.3 — Rebuild overlay. Sits over the previous PDF
                while a Rebuild runs so the operator sees activity
                instead of looking at a stale preview wondering if
                the click registered. */}
            {building && (
              <div style={{
                position: 'absolute', inset: 0,
                background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(2px)',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                gap: 12, color: 'white', zIndex: 4,
              }}>
                <span
                  aria-hidden
                  style={{
                    display: 'inline-block', width: 44, height: 44,
                    border: '4px solid rgba(255,255,255,0.2)',
                    borderTopColor: theme.brand,
                    borderRadius: '50%',
                    animation: 'dpReportSpin 0.9s linear infinite',
                  }}
                />
                <div style={{ fontSize: 14, fontWeight: 700 }}>Rebuilding…</div>
                <div style={{ fontSize: 11, opacity: 0.85 }}>
                  Replacing the preview below — hold tight.
                </div>
              </div>
            )}
          </>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'white' }}>
            Press Generate to build the PDF.
          </div>
        )}
      </div>

      {/* v0.10.0 - per-report distribution roster. Only rendered when
          this view is anchored to a saved report (?reportId=N). The
          live one-off preview (no reportId) doesn't have a roster to
          edit - it's an ad-hoc render. */}
      {reportId && (
        <TeamPanel
          kind="report"
          parentId={reportId}
          title="Report distribution"
          subtitle="Who this report goes to. Cloned from the project team when this report was created; edits here stay in this report."
        />
      )}

      {/* v0.103.0 — Style chooser. Applying re-renders the PDF straight
          away with the fresh settings (passed directly — React state
          lands async) and persists the choice: localStorage for the
          user-level default, settingsJson via Save settings for saved
          reports. */}
      {showStyleModal && (
        <ReportStyleModal
          initialStyleKey={settings.styleKey || 'signature'}
          initialColor={settings.brandColor || null}
          onClose={() => setShowStyleModal(false)}
          onApply={({ styleKey, brandColor }) => {
            const next = { ...settings, styleKey, brandColor };
            setSettings(next);
            saveStylePrefs({ styleKey, brandColor });
            setShowStyleModal(false);
            generate(next);
          }}
        />
      )}

      {showShareModal && (
        <ShareReportModal
          projectId={projectId}
          reportId={reportId}
          renderSettings={settings}
          onClose={() => setShowShareModal(false)}
        />
      )}

      {showSendModal && (
        <SendToInSiteModal
          projectId={projectId}
          reportId={reportId}
          defectIds={defectIdsFilter}
          reportTitle={savedReport ? savedReport.name : (settings.title || project?.Name || project?.name || 'this report')}
          defectCount={totalDefects}
          onClose={() => setShowSendModal(false)}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// SendToInSiteModal — v0.92.0. Confirm + send this report to the InSite office
// as a new enquiry (Mail Room). Calls /api/raise-enquiry, which loads the
// report's defects + photos and builds the InSpec intake payload SERVER-SIDE
// (the InSpec URL + shared secret never reach the browser). Same modal shell as
// ShareReportModal (backdrop + card + theme tokens) — no bespoke CSS.
function SendToInSiteModal({ projectId, reportId, defectIds, reportTitle, defectCount, onClose }) {
  const [status, setStatus] = useState('confirm'); // 'confirm' | 'sending' | 'sent' | 'error'
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  async function send() {
    setStatus('sending'); setError(null);
    try {
      const body = {
        projectId: Number(projectId),
        reportId: reportId ? Number(reportId) : null,
        defectIds: Array.isArray(defectIds) && defectIds.length ? defectIds : undefined,
      };
      const res = await apiSend('POST', '/api/raise-enquiry', body);
      // apiSend queues mutations when the network is down and resolves with a
      // marker instead of throwing — that is NOT a confirmed send, so surface it
      // as a retryable failure rather than a false success.
      if (res && res.queued) {
        setError('You appear to be offline — the report was not sent. Reconnect and try again.');
        setStatus('error');
        return;
      }
      if (!res || res.ok !== true) {
        setError((res && res.error) || 'InSite did not confirm the enquiry. Nothing was created.');
        setStatus('error');
        return;
      }
      setResult(res);
      setStatus('sent');
    } catch (e) {
      setError(e.message || String(e));
      setStatus('error');
    }
  }

  const items = Number(defectCount || 0);
  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ ...card, width: '100%', maxWidth: 460, maxHeight: '90vh', overflowY: 'auto', padding: 20, boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2 style={{ fontSize: 16, fontWeight: 800, margin: 0, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <Send size={16} /> {status === 'sent' ? 'Sent to InSite' : 'Send to InSite'}
          </h2>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 18, color: theme.textLight, padding: 0, lineHeight: 1 }} title="Close">×</button>
        </div>

        {status === 'sent' ? (
          <>
            <div style={{ marginBottom: 14, fontSize: 13, color: theme.text, lineHeight: 1.5 }}>
              Sent to InSite — the office will see it as a new enquiry in the Mail Room.
            </div>
            <div style={{ ...card, background: theme.bg, padding: 12, marginBottom: 14, boxShadow: 'none' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: theme.text }}>{result?.subject || reportTitle}</div>
              <div style={{ fontSize: 12, color: theme.textLight, marginTop: 4 }}>
                {(result?.defectCount ?? items)} item{(result?.defectCount ?? items) === 1 ? '' : 's'}
                {result?.attachmentsSent
                  ? ` · ${result.attachmentsSent} photo${result.attachmentsSent === 1 ? '' : 's'} attached`
                  : ' · no photos attached'}
                {result?.photosOmitted
                  ? ` · ${result.photosOmitted} over the size cap left out`
                  : ''}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={btnPrimary}>Done</button>
            </div>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 14, fontSize: 13, color: theme.text, lineHeight: 1.5 }}>
              Send <strong>{reportTitle || 'this report'}</strong> to InSite as a new enquiry?
              The office will see it in the Mail Room with {items} item{items === 1 ? '' : 's'} and any photos attached.
            </div>
            {status === 'error' && error && (
              <div style={{ marginBottom: 12, color: theme.danger, fontSize: 12 }}>{error}</div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button onClick={onClose} style={btnSecondary} disabled={status === 'sending'}>Cancel</button>
              <button
                onClick={send}
                disabled={status === 'sending'}
                style={{ ...btnPrimary, opacity: status === 'sending' ? 0.6 : 1, display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                {status === 'sending' ? 'Sending…' : status === 'error' ? 'Try again' : (<><Send size={14} /> Send to InSite</>)}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SettingsPanel — v0.9.0. Renders inline above the PDF preview. All state
// lives in the parent; this is just the UI.
function SettingsPanel({ settings, setSettings, updateFilter, toggleArrayItem, drawings, onClose, onSave, savingReport, onOpenStyle, onArchive }) {
  const s = settings;
  return (
    <div style={{ ...card, marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <h2 style={{ fontSize: 15, fontWeight: 800, margin: 0 }}>Report settings</h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {onArchive && (
            <button onClick={onArchive} style={btnSecondary} title="Archive this report">Archive report</button>
          )}
          {onSave && (
            <button onClick={onSave} disabled={savingReport} style={{ ...btnPrimary, opacity: savingReport ? 0.6 : 1 }}>
              {savingReport ? 'Saving…' : 'Save settings'}
            </button>
          )}
          <button onClick={onClose} style={btnSecondary}>Close</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
        {/* Filters */}
        <div>
          <SectionLabel>Filters</SectionLabel>
          <ChipFilter
            label="Status"
            options={STATUSES.map(v => ({ value: v, label: statusLabel(v) }))}
            selected={s.filters.status}
            onToggle={v => updateFilter('status', toggleArrayItem(s.filters.status, v))}
          />
          <ChipFilter
            label="Priority"
            options={PRIORITIES.map(v => ({ value: v, label: priorityLabel(v) }))}
            selected={s.filters.priority}
            onToggle={v => updateFilter('priority', toggleArrayItem(s.filters.priority, v))}
          />
          <ChipFilter
            label="Trade"
            options={TRADES.map(t => ({ value: t.key, label: t.label, color: t.color }))}
            selected={s.filters.trade}
            onToggle={v => updateFilter('trade', toggleArrayItem(s.filters.trade, v))}
            withSwatch
          />
          {drawings.length > 0 && (
            <ChipFilter
              label="Drawings"
              options={drawings.map(d => ({
                value: String(d.id),
                label: d.drawingNumber ? `${d.drawingNumber} — ${d.title || d.name || ''}`.slice(0, 28) : (d.title || d.name || `Drawing #${d.id}`),
              }))}
              selected={(s.filters.drawing || []).map(String)}
              onToggle={v => updateFilter('drawing', toggleArrayItem((s.filters.drawing || []).map(String), v).map(Number))}
            />
          )}
        </div>

        {/* Sections + Layout */}
        <div>
          <SectionLabel>Sections</SectionLabel>
          <ToggleRow label="Cover page"      value={s.includeCover}      onChange={v => setSettings(x => ({ ...x, includeCover: v }))} />
          <ToggleRow label="Photos"          value={s.includePhotos}     onChange={v => setSettings(x => ({ ...x, includePhotos: v }))} />
          <ToggleRow label="Plan thumbnails" value={s.includePlanThumbs} onChange={v => setSettings(x => ({ ...x, includePlanThumbs: v }))} />
          <ToggleRow label="Sign-off block"  value={s.includeSignoff}    onChange={v => setSettings(x => ({ ...x, includeSignoff: v }))} />

          <div style={{ height: 8 }} />
          <SectionLabel>Layout</SectionLabel>
          {/* v0.41.0 — Format picker. snag-list is the everyday layout;
              client-handover trims defect detail in favour of rectification
              evidence + sign-off; subbie-work-order groups by assignee for
              hand-out to trades. */}
          <Field label="Report format">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {[
                { value: 'snag-list',         label: 'Snag list',         hint: 'Default. Summary + per-drawing + per-trade + defect detail.' },
                { value: 'client-handover',   label: 'Client handover',   hint: 'For the client. Heavier on rectification evidence + sign-off.' },
                { value: 'subbie-work-order', label: 'Subbie work order', hint: 'For each trade. One section per assignee with their items + photos.' },
              ].map(opt => (
                <label key={opt.value} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', padding: 6, border: `1px solid ${s.format === opt.value ? theme.brand : theme.border}`, borderRadius: 6, background: s.format === opt.value ? theme.bg : 'transparent' }}>
                  <input
                    type="radio"
                    name="report-format"
                    value={opt.value}
                    checked={s.format === opt.value}
                    onChange={() => setSettings(x => ({ ...x, format: opt.value }))}
                    style={{ marginTop: 2 }}
                  />
                  <span>
                    <div style={{ fontSize: 13, fontWeight: 600, color: theme.text }}>{opt.label}</div>
                    <div style={{ fontSize: 11, color: theme.textLight }}>{opt.hint}</div>
                  </span>
                </label>
              ))}
            </div>
          </Field>
          {/* v0.103.0 — Report style: the visual picker lives in its own
              modal (live previews + accent colour). Surface it here too
              so the Settings flow can reach it. */}
          <Field label="Report style">
            <button
              type="button"
              onClick={onOpenStyle}
              style={{ ...btnSecondary, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}
            >
              <Palette size={13} />
              {resolveReportStyle(settings.styleKey).name} — change…
              <span style={{
                width: 10, height: 10, borderRadius: 3, display: 'inline-block',
                background: settings.brandColor || theme.brand,
                border: '1px solid rgba(0,0,0,0.15)',
              }} />
            </button>
          </Field>
          {/* v0.45.30 — Cover theme. Changes the visual styling of the
              cover only; body pages share the same chrome. Independent
              of format (above).
              v0.103.0 — only relevant to the Signature style: every
              other style pins its own cover treatment. */}
          {(settings.styleKey || 'signature') === 'signature' && (
          <Field label="Cover theme (Signature style)">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
              {[
                { value: 'cinematic', label: 'Cinematic',   hint: 'Dark navy + orange accent.' },
                { value: 'light',     label: 'Professional', hint: 'White cover, navy text.'  },
                { value: 'mono',      label: 'Mono',         hint: 'Black + white, minimal.' },
              ].map(opt => {
                const active = (s.theme || 'cinematic') === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setSettings(x => ({ ...x, theme: opt.value }))}
                    style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'stretch',
                      gap: 4, padding: 6, cursor: 'pointer', textAlign: 'left',
                      fontFamily: 'inherit',
                      border: `2px solid ${active ? theme.brand : theme.border}`,
                      borderRadius: 6,
                      background: active ? theme.bg : 'transparent',
                    }}
                  >
                    {/* Mini visual swatch — a tiny cover preview */}
                    <ThemeSwatch theme={opt.value} />
                    <div style={{ fontSize: 12, fontWeight: 700, color: theme.text }}>{opt.label}</div>
                    <div style={{ fontSize: 10, color: theme.textLight }}>{opt.hint}</div>
                  </button>
                );
              })}
            </div>
          </Field>
          )}
          <Field label="Paper size">
            <div style={{ display: 'flex', gap: 6 }}>
              {PAPER_SIZES.map(p => (
                <button
                  key={p.value}
                  type="button"
                  onClick={() => setSettings(x => ({ ...x, paperSize: p.value }))}
                  style={{
                    ...btnSecondary,
                    padding: '4px 10px',
                    fontSize: 12,
                    background: s.paperSize === p.value ? theme.bg : btnSecondary.background,
                    borderColor: s.paperSize === p.value ? theme.brand : btnSecondary.border,
                  }}
                >{p.label}</button>
              ))}
            </div>
          </Field>
          {/* v0.37.0 — Customer field removed from report settings (it now
              lives on the Project; edit it via the Project's Edit screen).
              Replaced with "Prepared by" — the operator who built the PDF.
              Autofills from the signed-in user, but editable per report. */}
          <Field label="Prepared by (prints on cover)">
            <input
              value={s.preparedBy || ''}
              onChange={e => setSettings(x => ({ ...x, preparedBy: e.target.value }))}
              style={inputStyle}
              placeholder="e.g. Brett Taylor"
              maxLength={120}
            />
          </Field>
          <Field label="Report title">
            <input
              value={s.title}
              onChange={e => setSettings(x => ({ ...x, title: e.target.value }))}
              style={inputStyle}
              placeholder="Defect Report"
            />
          </Field>
        </div>
      </div>

      <div style={{ marginTop: 10, fontSize: 11, color: theme.textLight }}>
        Click <b>Generate</b> / <b>Rebuild</b> on the header to re-render the PDF with these settings.
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: 11, fontWeight: 800, color: theme.textLight,
      textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6,
    }}>{children}</div>
  );
}
function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: theme.textLight, marginBottom: 4 }}>{label}</div>
      {children}
    </div>
  );
}
function ToggleRow({ label, value, onChange }) {
  return (
    <label style={{
      display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0',
      fontSize: 13, color: theme.text, cursor: 'pointer',
    }}>
      <input type="checkbox" checked={!!value} onChange={e => onChange(e.target.checked)} />
      {label}
    </label>
  );
}
function ChipFilter({ label, options, selected, onToggle, withSwatch }) {
  const sel = new Set((selected || []).map(String));
  const allOn = sel.size === 0; // empty = treat as "all"
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: theme.textLight, marginBottom: 4 }}>
        {label} <span style={{ color: theme.textLight, fontWeight: 400 }}>
          ({allOn ? 'all' : `${sel.size} selected`})
        </span>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {options.map(opt => {
          const active = sel.has(String(opt.value));
          return (
            <button
              key={opt.value}
              type="button"
              onClick={() => onToggle(opt.value)}
              style={{
                padding: '3px 8px',
                fontSize: 11, fontWeight: 600,
                borderRadius: 999,
                border: `1px solid ${active ? (opt.color || theme.brand) : theme.border}`,
                background: active ? (opt.color ? `${opt.color}22` : theme.bg) : theme.surface,
                color: active && opt.color ? opt.color : theme.text,
                cursor: 'pointer',
                fontFamily: 'inherit',
                display: 'inline-flex', alignItems: 'center', gap: 4,
              }}
            >
              {withSwatch && opt.color && (
                <span style={{ width: 8, height: 8, borderRadius: '50%', background: opt.color, display: 'inline-block' }} />
              )}
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%', fontSize: 13, padding: '6px 10px',
  border: `1px solid ${theme.border}`, borderRadius: 6,
  background: theme.surface, color: theme.text,
  boxSizing: 'border-box', outline: 'none',
};

// ---------------------------------------------------------------------------
// Field normalisers — bridge between API row shapes and the template's
// expected camelCase fields.
// v0.45.30 — Tiny cover swatch used in the Settings panel theme picker.
// Hand-drawn rectangle: page background + brand strip + a title bar.
// 40 × 56 px so three swatches fit comfortably side-by-side without
// crowding the picker.
function ThemeSwatch({ theme: themeKey }) {
  const presets = {
    cinematic: { bg: 'linear-gradient(180deg,#0f172a 0%,#1e293b 55%,#2d2d2d 100%)', strip: '#fb923c', stripVisible: true,  text: '#ffffff', rule: false },
    light:     { bg: '#fdfbf7',                                                       strip: '#fb923c', stripVisible: true,  text: '#0f172a', rule: false },
    mono:      { bg: '#ffffff',                                                       strip: '#000000', stripVisible: false, text: '#0a0a0a', rule: true  },
  };
  const p = presets[themeKey] || presets.cinematic;
  return (
    <div style={{
      position: 'relative',
      height: 56, width: '100%',
      background: p.bg,
      border: '1px solid rgba(0,0,0,0.15)',
      borderRadius: 4,
      overflow: 'hidden',
      marginBottom: 2,
    }}>
      {p.stripVisible && (
        <div style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 5, background: p.strip }} />
      )}
      {/* Doc label */}
      <div style={{
        position: 'absolute', left: 6, top: 8,
        fontSize: 6, fontWeight: 800, letterSpacing: '0.05em',
        color: p.strip,
      }}>REPORT</div>
      {p.rule && <div style={{ position: 'absolute', left: 6, top: 16, width: 14, height: 1.2, background: p.strip }} />}
      {/* Title bar */}
      <div style={{ position: 'absolute', left: 6, top: 22, width: '60%', height: 6, background: p.text, opacity: 0.85, borderRadius: 1 }} />
      {/* Subtitle */}
      <div style={{ position: 'absolute', left: 6, top: 32, width: '40%', height: 3, background: p.text, opacity: 0.55, borderRadius: 1 }} />
      {/* KPI strip */}
      <div style={{ position: 'absolute', left: 6, right: p.stripVisible ? 10 : 6, bottom: 8, display: 'flex', gap: 2 }}>
        {[0, 1, 2, 3, 4].map(i => (
          <div key={i} style={{ flex: 1, height: 8, background: p.text, opacity: themeKey === 'mono' ? 0 : (themeKey === 'cinematic' ? 0.08 : 0.10), borderRadius: 1 }} />
        ))}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────
// v0.45.29 — ReportLoadingState.
//
// Brett (screenshot of the bare "Loading project, drawings, defects
// and photos…" text card): "Can we have something more interesting
// on this page while the report is loading."
//
// Replaces the one-line spinner with three things stacked vertically:
//   1. A skeleton preview of the report — pulsing placeholders for
//      the title bar, KPI strip, and a few defect rows. Modern SaaS
//      pattern: shows the user what's about to appear so the page
//      doesn't feel empty.
//   2. A small animated pin dropping onto paper at the centre.
//      Matches the brand vocabulary and gives a focal point.
//   3. A real stage checklist driven by loadStage. Each row ticks
//      to green as that step completes, so the user can see actual
//      progress instead of guessing.
//
// All inline — no extra assets, no new deps. Keyframes scoped to a
// local <style> block so they don't leak.

function ReportLoadingState({ stage, onBack, hasReportId }) {
  // Stages in canonical order. 'team' only shows when there's a
  // reportId (otherwise the load skips that step).
  const STAGES = [
    { key: 'project', label: 'Project, drawings & defects' },
    { key: 'photos',  label: 'Defect photos' },
    ...(hasReportId
      ? [
          { key: 'report', label: 'Saved report settings' },
          { key: 'team',   label: 'Distribution list' },
        ]
      : []),
  ];
  // Index of the current stage; everything before it is done.
  const currentIdx = STAGES.findIndex(s => s.key === stage);

  return (
    <div>
      <button onClick={onBack} style={{ ...btnSecondary, marginBottom: 12 }}>← Back</button>

      <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
        {/* ── Skeleton preview of the report header ── */}
        <div style={{ padding: '18px 20px', borderBottom: `1px solid ${theme.border}` }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <SkeletonBlock w={28} h={28} radius={6} />
            <div style={{ flex: 1 }}>
              <SkeletonBlock w="60%" h={20} />
              <div style={{ height: 6 }} />
              <SkeletonBlock w="40%" h={12} />
            </div>
          </div>
          {/* KPI strip — 5 cells */}
          <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
            {[0, 1, 2, 3, 4].map(i => (
              <div key={i} style={{ flex: 1, padding: 10, background: theme.surface, borderRadius: 6 }}>
                <SkeletonBlock w="50%" h={18} />
                <div style={{ height: 6 }} />
                <SkeletonBlock w="80%" h={10} />
              </div>
            ))}
          </div>
        </div>

        {/* ── Centrepiece: animated pin dropping onto paper ── */}
        <div style={{
          padding: '24px 20px',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          gap: 28, flexWrap: 'wrap',
        }}>
          <DropPinAnimation />

          {/* ── Stage checklist ── */}
          <div style={{
            minWidth: 260,
            display: 'flex', flexDirection: 'column', gap: 8,
          }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: theme.textLight }}>
              Building your report
            </div>
            {STAGES.map((s, i) => {
              const done = currentIdx > i;
              const live = currentIdx === i;
              return (
                <div key={s.key} style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  fontSize: 13,
                  color: done ? theme.text : (live ? theme.text : theme.textLight),
                  opacity: done || live ? 1 : 0.55,
                }}>
                  {done ? (
                    <span style={{
                      width: 18, height: 18, borderRadius: '50%',
                      background: theme.success, color: '#fff',
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 800, flexShrink: 0,
                    }}>✓</span>
                  ) : live ? (
                    <span style={{
                      width: 18, height: 18, borderRadius: '50%',
                      border: `2px solid ${theme.brand}`,
                      borderRightColor: 'transparent',
                      animation: 'rpt-spin 0.7s linear infinite',
                      flexShrink: 0,
                    }} />
                  ) : (
                    <span style={{
                      width: 18, height: 18, borderRadius: '50%',
                      border: `2px solid ${theme.border}`,
                      flexShrink: 0,
                    }} />
                  )}
                  <span style={{ fontWeight: live ? 700 : 500 }}>{s.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* ── Skeleton defect rows ── */}
        <div style={{ padding: '12px 20px 22px', display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[0, 1, 2, 3].map(i => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 0',
              borderTop: i === 0 ? 'none' : `1px solid ${theme.border}`,
            }}>
              <SkeletonBlock w={36} h={36} radius={6} />
              <div style={{ flex: 1 }}>
                <SkeletonBlock w={`${70 - i * 8}%`} h={12} />
                <div style={{ height: 6 }} />
                <SkeletonBlock w={`${40 + i * 5}%`} h={10} />
              </div>
              <SkeletonBlock w={70} h={20} radius={99} />
            </div>
          ))}
        </div>
      </div>

      {/* Keyframes — scoped to this card. */}
      <style>{`
        @keyframes rpt-pulse {
          0%   { opacity: 0.55; }
          50%  { opacity: 1;    }
          100% { opacity: 0.55; }
        }
        @keyframes rpt-spin {
          to { transform: rotate(360deg); }
        }
        @keyframes rpt-drop {
          0%   { transform: translateY(-32px) rotate(-8deg); opacity: 0; }
          30%  { opacity: 1; }
          70%  { transform: translateY(0)     rotate(0deg);  }
          80%  { transform: translateY(-3px)  rotate(0deg);  }
          100% { transform: translateY(0)     rotate(0deg);  opacity: 1; }
        }
        @keyframes rpt-shadow {
          0%, 30% { transform: scaleX(0.6); opacity: 0.15; }
          70%     { transform: scaleX(1.0); opacity: 0.45; }
          100%    { transform: scaleX(0.85); opacity: 0.35; }
        }
      `}</style>
    </div>
  );
}

function SkeletonBlock({ w = '100%', h = 12, radius = 4 }) {
  return (
    <div style={{
      width: w, height: h, borderRadius: radius,
      background: `linear-gradient(90deg, ${theme.border} 0%, ${theme.bg} 50%, ${theme.border} 100%)`,
      animation: 'rpt-pulse 1.4s ease-in-out infinite',
    }} />
  );
}

// Small SVG: a sheet of paper with a pin dropping onto it on a 1.6s loop.
// All vector — scales with the parent's font-size if needed; default is
// 140 × 140 viewport-relative.
function DropPinAnimation() {
  return (
    <div style={{ position: 'relative', width: 140, height: 140, flexShrink: 0 }}>
      {/* Paper */}
      <svg viewBox="0 0 140 140" width="140" height="140" aria-hidden="true" style={{ position: 'absolute', inset: 0 }}>
        <defs>
          <linearGradient id="rpt-paper" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#fcf9ee" />
            <stop offset="1" stopColor="#eee6c6" />
          </linearGradient>
        </defs>
        {/* sheet shadow */}
        <rect x="22" y="30" width="100" height="92" rx="3" fill="rgba(15,23,42,0.12)" />
        {/* sheet */}
        <rect x="20" y="28" width="100" height="92" rx="3" fill="url(#rpt-paper)" stroke="#c9c0a3" strokeWidth="0.8" />
        {/* lines */}
        <g stroke="#c9c0a3" strokeWidth="0.6" opacity="0.7">
          <line x1="28" y1="46"  x2="112" y2="46" />
          <line x1="28" y1="58"  x2="112" y2="58" />
          <line x1="28" y1="70"  x2="112" y2="70" />
          <line x1="28" y1="82"  x2="112" y2="82" />
          <line x1="28" y1="94"  x2="112" y2="94" />
          <line x1="28" y1="106" x2="112" y2="106" />
        </g>
      </svg>

      {/* Pulsing shadow on the paper where the pin will land */}
      <span style={{
        position: 'absolute',
        left: '50%', top: 88,
        width: 28, height: 5, borderRadius: '50%',
        background: 'rgba(0,0,0,0.45)',
        transform: 'translateX(-50%) scaleX(0.85)',
        transformOrigin: 'center',
        animation: 'rpt-shadow 1.6s ease-out infinite',
      }} />

      {/* The pin — bouncing in */}
      <span style={{
        position: 'absolute',
        left: '50%', top: 28,
        width: 22, height: 60,
        transform: 'translateX(-50%)',
        animation: 'rpt-drop 1.6s ease-out infinite',
      }}>
        <svg width="22" height="60" viewBox="0 0 40 60" style={{ overflow: 'visible' }} aria-hidden="true">
          <line x1="12" y1="55" x2="22" y2="24" stroke="#5b6470" strokeWidth="2.6" strokeLinecap="round" />
          <line x1="12.5" y1="54" x2="21.5" y2="24" stroke="rgba(255,255,255,0.35)" strokeWidth="0.8" strokeLinecap="round" />
          <circle cx="22" cy="18" r="12" fill="#fb923c" stroke="rgba(0,0,0,0.25)" strokeWidth="1" />
          <ellipse cx="18" cy="13" rx="4.5" ry="2.8" fill="rgba(255,255,255,0.55)" />
        </svg>
      </span>
    </div>
  );
}
